<script setup lang="ts">
import { ref } from 'vue'
import { CLINIC_CRF_TEMPLATES, type ClinicCrfTemplate } from '@clinic-crf/contract'
import {
  Activity, ArrowRight, BookOpenText, ClipboardCheck, FileJson, FlaskConical,
  FolderOpen, LockKeyhole, Plus, ShieldAlert, Sparkles, UploadCloud,
  SquareCheckBig,
} from 'lucide-vue-next'
import workflowImage from '../assets/clinic-ecrf-workflow.webp'

defineProps<{ resumeLabel?: string; importError?: string }>()
const emit = defineEmits<{ choose: [template: ClinicCrfTemplate]; resume: []; import: [file: File] }>()
const dragging = ref(false)
const input = ref<HTMLInputElement | null>(null)

function iconFor(category: ClinicCrfTemplate['category']) {
  return { start: Plus, screening: ClipboardCheck, visit: Activity, interview: BookOpenText, safety: ShieldAlert }[category]
}

function receive(files: FileList | null): void {
  dragging.value = false
  const file = files?.[0]
  if (file) emit('import', file)
}
</script>

<template>
  <main class="welcome">
    <nav class="welcome-nav" aria-label="Nawigacja główna">
      <a href="#main" class="brand" aria-label="StrefaBadań eCRF Maker — strona startowa"><span class="brand-mark"><SquareCheckBig :size="21" :stroke-width="2.2" /></span><span><strong>StrefaBadań</strong><small>eCRF Studio</small></span></a>
      <div class="nav-actions"><span class="open-badge"><LockKeyhole :size="14" /> Open source · local-first</span><a class="button button-quiet" href="https://clinic.strefaankiet.pl" target="_blank" rel="noopener noreferrer">Poznaj pełny eCRF <ArrowRight :size="16" /></a></div>
    </nav>

    <section id="main" class="welcome-hero">
      <div class="hero-copy">
        <span class="hero-kicker"><Sparkles :size="15" /> Projektuj zgodnie z kontraktem clinic-crf.v2</span>
        <h1>Zbuduj czytelny eCRF.<br /><em>Zanim trafi do badania.</em></h1>
        <p>Twórz, testuj i udostępniaj definicje formularzy klinicznych bez konta, instalacji ani wysyłania treści do serwera.</p>
        <div class="hero-trust"><span><LockKeyhole :size="17" /> Dane zostają w przeglądarce</span><span><FileJson :size="17" /> Otwarty format JSON</span><span><FlaskConical :size="17" /> Sandbox bez danych pacjentów</span></div>
      </div>
      <figure class="hero-visual">
        <div class="workflow-visual-frame">
          <img :src="workflowImage" alt="Zespół badania klinicznego analizujący cyfrowy obieg danych eCRF" />
        </div>
        <figcaption><span>Bezpieczny obieg eCRF</span><strong>Projekt · walidacja · publikacja</strong></figcaption>
      </figure>
    </section>

    <section class="start-workspace" aria-labelledby="start-heading">
      <div class="section-title"><div><span class="eyebrow">Szybki start</span><h2 id="start-heading">Od czego zaczynamy?</h2></div><p>Każdy szablon jest poprawnym, syntetycznym pakietem. Możesz zmienić wszystko.</p></div>
      <button v-if="resumeLabel" class="resume-card" type="button" @click="emit('resume')"><span class="resume-icon"><FolderOpen :size="23" /></span><span><small>Ostatni lokalny projekt</small><strong>{{ resumeLabel }}</strong><em>Wznów automatycznie zapisany szkic</em></span><ArrowRight :size="21" /></button>

      <div class="template-grid">
        <button v-for="template in CLINIC_CRF_TEMPLATES" :key="template.id" class="template-card" type="button" @click="emit('choose', template)">
          <span :class="['template-icon', `category-${template.category}`]"><component :is="iconFor(template.category)" :size="22" /></span>
          <span class="template-copy"><strong>{{ template.title }}</strong><small>{{ template.description }}</small><em>{{ template.package.definition.fields.length }} pól · {{ template.package.presentation?.sections.length ?? 1 }} sekcje</em></span>
          <ArrowRight :size="19" />
        </button>
      </div>

      <div class="import-divider"><span>albo otwórz istniejącą definicję</span></div>
      <div
        :class="['dropzone', { dragging }]"
        role="button"
        tabindex="0"
        @click="input?.click()"
        @keydown.enter="input?.click()"
        @keydown.space.prevent="input?.click()"
        @dragenter.prevent="dragging = true"
        @dragover.prevent="dragging = true"
        @dragleave.prevent="dragging = false"
        @drop.prevent="receive($event.dataTransfer?.files ?? null)"
      >
        <UploadCloud :size="28" /><div><strong>Przeciągnij plik clinic-crf JSON</strong><span>lub kliknij, aby wybrać · maks. 2 MB</span></div>
        <input ref="input" class="visually-hidden" type="file" accept="application/json,.json,.clinic-crf.json" @change="receive(($event.target as HTMLInputElement).files)" />
      </div>
      <p v-if="importError" class="import-error" role="alert">{{ importError }}</p>
    </section>

    <footer class="welcome-footer"><span>StrefaBadań eCRF Maker · MPL-2.0</span><span>Bez telemetrii · bez zewnętrznych zasobów · dane wyłącznie lokalne</span></footer>
  </main>
</template>
